import { generatePath } from 'react-router-dom';
import routes from '~/routes';
import {
  EXCLUDED_FROM_PACKAGE_REASON,
  NO_FORMATION_REASON,
  UNSCHEDULED_REASON,
  reasonParts,
  type ReasonLink,
  type ReasonPart,
} from './definitions';

// The reasons that link to their fix (INS-12687), keyed on the server's exact strings, which
// Rails specs pin to their Ruby constants, so a reworded reason fails a spec rather than
// silently rendering unlinked.

/** What the caller knows about where each fix lives. Every href is SERVED, never built. */
export interface ReasonFixContext {
  /** The package whose wizard an exclusion links to; null/absent → an exclusion is unlinked. */
  packageId?: number | null;
  /** The row's served well href (`slotIdHref` / tearsheet `header.slot.wellHref`). */
  wellHref?: string | null;
  /** The row's served DPS scenario href. */
  dpsHref?: string | null;
  /** The row's "No formation assigned" verdict: the well IS landed, its interval unmapped (INS-13385). */
  landedWithoutFormation?: boolean | null;
  /** The row's reasons as a list, when the server sent one (INS-12684). */
  unvaluedReasons?: readonly string[] | null;
}

interface ReasonFix {
  label: string;
  hrefFor: (ctx: ReasonFixContext) => string | null | undefined;
}

const REASON_FIXES: Record<string, ReasonFix> = {
  [NO_FORMATION_REASON]: {
    label: 'Land the well on its well page',
    // A landed well's fix is mapping its interval in the type-curve scenario, not landing it
    // (the definition says so), so it gets no "land the well" link.
    hrefFor: (ctx) => (ctx.landedWithoutFormation ? null : ctx.wellHref),
  },
  [EXCLUDED_FROM_PACKAGE_REASON]: {
    label: 'Edit exclusions in the package wizard',
    hrefFor: ({ packageId }) => {
      if (packageId === null || packageId === undefined) {
        return null;
      }
      return generatePath(routes.editPackagePath, { id: packageId });
    },
  },
  [UNSCHEDULED_REASON]: {
    label: 'Open the Development Pace Scenario',
    hrefFor: (ctx) => ctx.dpsHref,
  },
};

/**
 * Where exactly one reason gets fixed, or null when it has no fix or its href was not
 * served.
 *
 * @param {string} reason - One reason, matched exactly.
 * @param {ReasonFixContext} ctx - The package id and the row's served hrefs.
 * @return {ReasonLink | null} The fix link, or null.
 */
function linkFor(reason: string, ctx: ReasonFixContext): ReasonLink | null {
  // Own keys only, so a reason like "constructor" cannot resolve to a prototype member.
  const fix = Object.prototype.hasOwnProperty.call(REASON_FIXES, reason)
    ? REASON_FIXES[reason]
    : null;
  const href = fix?.hrefFor(ctx);
  return fix && href ? { href, label: fix.label } : null;
}

/**
 * The row's reasons (`reasonParts`), each with where it gets fixed, so every fixable cause gets
 * its own link (INS-12687). No fuzzy matching — a near miss stays unlinked. Null when no reason
 * has a fix, so the caller renders the reason as plain text.
 *
 * @param {string | null | undefined} reason - The row's unvalued reason, joined.
 * @param {ReasonFixContext} ctx - The package id, the row's served hrefs and its reasons list.
 * @return {ReasonPart[] | null} The parts, or null when none links anywhere.
 */
export function reasonFixParts(
  reason: string | null | undefined,
  ctx: ReasonFixContext
): ReasonPart[] | null {
  const parts = reasonParts(reason, ctx.unvaluedReasons).map((text) => ({
    text,
    link: linkFor(text, ctx),
  }));
  return parts.some((part) => part.link) ? parts : null;
}

/**
 * Every fix link for a reason, in the reason's order — for a surface that lists the links
 * beside the reason rather than inside it (the tearsheet notice).
 *
 * @param {string | null | undefined} reason - The row's unvalued reason, joined.
 * @param {ReasonFixContext} ctx - The package id, the row's served hrefs and its reasons list.
 * @return {ReasonLink[]} The fix links; empty when there are none.
 */
export function reasonFixLinks(
  reason: string | null | undefined,
  ctx: ReasonFixContext
): ReasonLink[] {
  return (reasonFixParts(reason, ctx) ?? []).flatMap((part) =>
    part.link ? [part.link] : []
  );
}
